import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle2, Sparkles, Clock, AlertTriangle, 
  Tag, TrendingUp, Calendar, Mail, FileText,
  Square, CheckSquare, Bot, Newspaper, Bell,
  XCircle, Archive, BookmarkPlus, Eye, EyeOff,
  ThumbsDown, Zap, ArrowRight, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation as useLanguageContext } from '../../../context/LanguageContext';

/**
 * AI Brief Section - ADAPTIVE Design
 * 
 * Changes based on email type:
 * - client_matter: Full analysis with case creation
 * - system_notification: Quick dismiss options
 * - newsletter: Read later / dismiss
 * - spam: One-click dismiss
 * - unknown: Standard analysis
 */

// Email classification types
type EmailType = 'client_matter' | 'system_notification' | 'newsletter' | 'internal' | 'spam' | 'unknown';

interface SuggestedAction {
  id: string;
  label: string;
  checked: boolean;
  icon: React.ReactNode;
}

// Detect email type from brief data
const detectEmailType = (brief: any, senderEmail?: string): EmailType => {
  if (!brief) return 'unknown';
  
  const category = brief?.category?.toLowerCase() || '';
  const sender = (senderEmail || brief?.sender_email || '').toLowerCase();
  const subject = (brief?.subject || '').toLowerCase();
  
  // System notifications
  const systemDomains = [
    'teams.microsoft.com', 'slack.com', 'notifications', 'noreply',
    'calendar-notification', 'no-reply', 'mailer-daemon', 'postmaster',
    'linkedin.com', 'github.com', 'jira', 'confluence', 'trello'
  ];
  if (systemDomains.some(domain => sender.includes(domain))) {
    return 'system_notification';
  }
  
  // Newsletters
  const newsletterKeywords = ['newsletter', 'nyhetsbrev', 'digest', 'weekly', 'monthly', 'update', 'subscription'];
  if (newsletterKeywords.some(kw => category.includes(kw) || subject.includes(kw) || sender.includes(kw))) {
    return 'newsletter';
  }
  
  // Spam indicators
  if (brief?.risk_level === 'SPAM' || category === 'spam') {
    return 'spam';
  }
  
  // Client matter (high priority, legal categories, etc.)
  const clientCategories = ['legal', 'juridisk', 'client', 'klient', 'case', 'sak', 'contract', 'kontrakt'];
  if (clientCategories.some(cat => category.includes(cat)) || brief?.priority === 'HIGH' || brief?.priority === 'URGENT') {
    return 'client_matter';
  }
  
  // Internal communications
  if (category.includes('internal') || category.includes('intern')) {
    return 'internal';
  }
  
  return 'unknown';
};

const AIBriefSection = ({ 
  brief, 
  attachments = [], 
  onOpenAttachment,
  onCreateCase,
  onOpenFullAnalysis,
  onReanalyze,
  onDismiss,
  onArchive,
  onReadLater,
  onAutoRule,
  actionInProgress,
  isReanalyzing = false,
  darkMode = false,
  senderEmail
}: { 
  brief: any; 
  attachments?: any[]; 
  onOpenAttachment?: (attachment: any) => void; 
  onCreateCase: () => void; 
  onOpenFullAnalysis: () => void; 
  onReanalyze?: () => void;
  onDismiss?: (reason: string) => void;
  onArchive?: () => void;
  onReadLater?: () => void;
  onAutoRule?: (sender: string) => void;
  actionInProgress: string | null;
  isReanalyzing?: boolean;
  darkMode?: boolean;
  senderEmail?: string;
}) => {
  const { t } = useTranslation('inbox');
  const { language } = useLanguageContext();
  const isEnglish = language === 'en';
  
  // Translation helper
  const tr = (en: string, no: string) => isEnglish ? en : no;
  
  // Detect email type
  const emailType = detectEmailType(brief, senderEmail);
  
  // Suggested actions state - adapts based on email type
  const getDefaultActions = (): SuggestedAction[] => {
    if (emailType === 'client_matter' || emailType === 'unknown') {
      return [
        { id: 'create-case', label: tr('Create case from email', 'Opprett sak fra e-post'), checked: false, icon: <FileText className="w-3.5 h-3.5" /> },
        { id: 'schedule', label: tr('Schedule follow-up', 'Planlegg oppfølging'), checked: false, icon: <Calendar className="w-3.5 h-3.5" /> },
        { id: 'request-info', label: tr('Request more information', 'Be om mer informasjon'), checked: false, icon: <Mail className="w-3.5 h-3.5" /> },
      ];
    }
    return [];
  };
  
  const [actions, setActions] = useState<SuggestedAction[]>(getDefaultActions());
  const [showAutoRulePrompt, setShowAutoRulePrompt] = useState(false);
  const [dismissReason, setDismissReason] = useState<string | null>(null);
  
  if (!brief) return null;
  
  const toggleAction = (id: string) => {
    setActions(prev => prev.map(a => 
      a.id === id ? { ...a, checked: !a.checked } : a
    ));
    
    if (id === 'create-case') {
      const action = actions.find(a => a.id === id);
      if (!action?.checked) {
        onCreateCase();
      }
    }
  };
  
  // Handle dismiss with learning prompt
  const handleDismiss = (reason: string) => {
    setDismissReason(reason);
    onDismiss?.(reason);
    setShowAutoRulePrompt(true);
  };
  
  // Handle auto-rule creation
  const handleCreateAutoRule = () => {
    const sender = senderEmail || brief?.sender_email;
    if (sender) {
      onAutoRule?.(sender);
      toast.success(tr(`Emails from ${sender} will now be archived automatically`, `E-poster fra ${sender} vil nå arkiveres automatisk`));
    }
    setShowAutoRulePrompt(false);
  };
  
  // Priority styling
  const getPriorityStyle = (priority: string) => {
    switch (priority?.toUpperCase()) {
      case 'URGENT':
      case 'HIGH':
        return 'bg-red-50 text-red-600 border-red-100';
      case 'MEDIUM':
      case 'NORMAL':
        return 'bg-amber-50 text-amber-600 border-amber-100';
      case 'LOW':
        return 'bg-green-50 text-green-600 border-green-100';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-100';
    }
  };
  
  const getPriorityLabel = (priority: string) => {
    switch (priority?.toUpperCase()) {
      case 'URGENT': return tr('Urgent', 'Haster');
      case 'HIGH': return tr('High', 'Høy');
      case 'MEDIUM':
      case 'NORMAL': return tr('Normal', 'Normal');
      case 'LOW': return tr('Low', 'Lav');
      default: return tr('Normal', 'Normal');
    }
  };
  
  // Extract key info
  const category = brief?.category || tr('General', 'Generell');
  const priority = brief?.priority || 'NORMAL';
  const deadline = brief?.deadline_detected;
  const summary = brief?.what_this_is || brief?.summary;
  const keyPoints = brief?.key_facts || brief?.known_facts || brief?.what_we_know || [];
  const hasRisks = (brief?.red_flags?.length > 0) || (brief?.yellow_flags?.length > 0);
  const senderName = brief?.sender_name || tr('Unknown sender', 'Ukjent avsender');
  
  // Get type-specific styling and content
  const getTypeConfig = () => {
    switch (emailType) {
      case 'system_notification':
        return {
          badge: { icon: Bell, label: tr('System notification', 'Systemvarsel'), color: 'bg-slate-100 text-slate-600 border-slate-200' },
          summary: tr(`Automatic notification from ${senderName}. Not client related.`, `Automatisk varsel fra ${senderName}. Ikke klientrelatert.`),
          showActions: false,
          showCTA: false
        };
      case 'newsletter':
        return {
          badge: { icon: Newspaper, label: tr('Newsletter', 'Nyhetsbrev'), color: 'bg-blue-50 text-blue-600 border-blue-200' },
          summary: summary || tr(`Newsletter from ${senderName}.`, `Nyhetsbrev fra ${senderName}.`),
          showActions: false,
          showCTA: false
        };
      case 'spam':
        return {
          badge: { icon: XCircle, label: tr('Possible spam', 'Mulig spam'), color: 'bg-red-50 text-red-600 border-red-200' },
          summary: tr('This email appears to be spam or unwanted advertising.', 'Denne e-posten ser ut som spam eller uønsket reklame.'),
          showActions: false,
          showCTA: false
        };
      case 'internal':
        return {
          badge: { icon: Mail, label: tr('Internal', 'Intern'), color: 'bg-purple-50 text-purple-600 border-purple-200' },
          summary: summary || tr('Internal communication.', 'Intern kommunikasjon.'),
          showActions: false,
          showCTA: false
        };
      case 'client_matter':
        return {
          badge: { icon: Sparkles, label: tr('Client case', 'Klientsak'), color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
          summary: summary,
          showActions: true,
          showCTA: true
        };
      default:
        return {
          badge: null,
          summary: summary,
          showActions: true,
          showCTA: true
        };
    }
  };
  
  const typeConfig = getTypeConfig();
  
  // ═══════════════════════════════════════════════════════════════════
  // RENDER: System Notification / Newsletter / Spam - Quick Dismiss UI
  // ═══════════════════════════════════════════════════════════════════
  if (emailType === 'system_notification' || emailType === 'newsletter' || emailType === 'spam') {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        {/* Type Badge */}
        <div className="px-4 pt-2 pb-4">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${typeConfig.badge?.color}`}>
            {typeConfig.badge && <typeConfig.badge.icon className="w-3.5 h-3.5" />}
            {typeConfig.badge?.label}
          </div>
        </div>
        
        {/* Summary */}
        <div className="px-4 pb-5">
          <p className="text-sm text-gray-600 leading-relaxed">
            {typeConfig.summary}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            {tr('From', 'Fra')}: {senderName}
          </p>
        </div>
        
        {/* Quick Actions - Prominent */}
        <div className="px-4 pb-4 border-t border-gray-100 pt-4">
          <div className="grid grid-cols-2 gap-2">
            {/* Dismiss Button */}
            <button
              onClick={() => handleDismiss(emailType)}
              className="flex items-center justify-center gap-2 h-11 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-all"
            >
              <EyeOff className="w-4 h-4" />
              <span className="text-sm">Ignorer</span>
            </button>
            
            {/* Archive Button */}
            <button
              onClick={() => onArchive?.()}
              className="flex items-center justify-center gap-2 h-11 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-all"
            >
              <Archive className="w-4 h-4" />
              <span className="text-sm">Arkiver</span>
            </button>
          </div>
          
          {/* Read Later for newsletters */}
          {emailType === 'newsletter' && (
            <button
              onClick={() => onReadLater?.()}
              className="w-full flex items-center justify-center gap-2 h-10 mt-2 text-blue-600 hover:bg-blue-50 font-medium rounded-xl transition-all"
            >
              <BookmarkPlus className="w-4 h-4" />
              <span className="text-sm">Les senere</span>
            </button>
          )}
        </div>
        
        {/* Auto-Rule Prompt */}
        <AnimatePresence>
          {showAutoRulePrompt && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="px-4 pb-4"
            >
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-4 border border-violet-100">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-violet-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      Lær av dette?
                    </p>
                    <p className="text-xs text-gray-600 mb-3">
                      Vil du alltid arkivere e-post fra {senderName} automatisk?
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleCreateAutoRule}
                        className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium rounded-lg transition-colors"
                      >
                        Ja, alltid
                      </button>
                      <button
                        onClick={() => setShowAutoRulePrompt(false)}
                        className="px-3 py-1.5 text-gray-600 hover:text-gray-800 text-xs font-medium transition-colors"
                      >
                        Nei takk
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Misclassified? Link */}
        <div className="px-4 pb-4">
          <button
            onClick={onOpenFullAnalysis}
            className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-2 transition-all"
          >
            {tr('Wrong classification? See full analysis →', 'Feil klassifisering? Se full analyse →')}
          </button>
        </div>
      </motion.div>
    );
  }
  
  // ═══════════════════════════════════════════════════════════════════
  // RENDER: Client Matter / Unknown - Full Analysis UI
  // ═══════════════════════════════════════════════════════════════════
  return (
    <motion.div 
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <div className="space-y-0">
        {/* Type Badge for client matters */}
        {typeConfig.badge && (
          <div className="px-4 pt-2 pb-2">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${typeConfig.badge.color}`}>
              <typeConfig.badge.icon className="w-3.5 h-3.5" />
              {typeConfig.badge.label}
            </div>
          </div>
        )}
        
        {/* ═══════════════════════════════════════════════════════
            AI SUMMARY
           ═══════════════════════════════════════════════════════ */}
        <div className="px-4 pt-2 pb-5">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{tr('AI Summary', 'AI Oppsummering')}</h3>
          <p className="text-sm text-gray-700 leading-relaxed">
            {typeConfig.summary || tr('No summary available', 'Ingen oppsummering tilgjengelig')}
          </p>
          
          {/* Key points */}
          {keyPoints.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-50 space-y-2">
              {keyPoints.slice(0, 3).map((point: string, idx: number) => (
                <div key={idx} className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-gray-500">{point}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* ═══════════════════════════════════════════════════════
            EXTRACTED INFO
           ═══════════════════════════════════════════════════════ */}
        <div className="px-4 py-5 border-t border-gray-100">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">
            {tr('Extracted Info', 'Uttrukket Info')}
          </h3>
          
          <div className="space-y-3.5">
            {/* Category */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Tag className="w-3.5 h-3.5 text-gray-400" />
                <span>{tr('Category', 'Kategori')}</span>
              </div>
              <span className="px-2.5 py-1 bg-purple-50 text-purple-600 rounded-lg text-xs font-medium">
                {category}
              </span>
            </div>
            
            {/* Priority */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <TrendingUp className="w-3.5 h-3.5 text-gray-400" />
                <span>{tr('Priority', 'Prioritet')}</span>
              </div>
              <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${getPriorityStyle(priority)}`}>
                {getPriorityLabel(priority)}
              </span>
            </div>
            
            {/* Deadline */}
            {deadline && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                  <span>{tr('Deadline', 'Frist')}</span>
                </div>
                <span className="px-2.5 py-1 bg-red-50 text-red-600 rounded-lg text-xs font-medium">
                  {deadline}
                </span>
              </div>
            )}
            
            {/* Risk indicator */}
            {hasRisks && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <AlertTriangle className="w-3.5 h-3.5 text-gray-400" />
                  <span>{tr('Risk level', 'Risikonivå')}</span>
                </div>
                <span className="px-2.5 py-1 bg-amber-50 text-amber-600 rounded-lg text-xs font-medium">
                  {brief?.red_flags?.length > 0 ? tr('High', 'Høy') : tr('Medium', 'Middels')}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* ═══════════════════════════════════════════════════════
            SUGGESTED ACTIONS
           ═══════════════════════════════════════════════════════ */}
        {typeConfig.showActions && actions.length > 0 && (
          <div className="px-4 py-5 border-t border-gray-100">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">
              {tr('Suggested Actions', 'Foreslåtte Handlinger')}
            </h3>
            
            <div className="space-y-2.5">
              {actions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => toggleAction(action.id)}
                  disabled={actionInProgress === 'create' && action.id === 'create-case'}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${
                    action.checked 
                      ? 'bg-purple-50/80 border border-purple-200' 
                      : 'bg-gray-50/50 hover:bg-gray-100/80 border border-gray-200'
                  }`}
                >
                  <div className={`flex-shrink-0 ${action.checked ? 'text-purple-600' : 'text-gray-400'}`}>
                    {action.checked ? (
                      <CheckSquare className="w-4.5 h-4.5" />
                    ) : (
                      <Square className="w-4.5 h-4.5" />
                    )}
                  </div>
                  <div className={`flex-shrink-0 ${action.checked ? 'text-purple-600' : 'text-gray-400'}`}>
                    {action.icon}
                  </div>
                  <span className={`text-sm ${action.checked ? 'text-purple-700 font-semibold' : 'text-gray-600 font-medium'}`}>
                    {action.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* ═══════════════════════════════════════════════════════
            PRIMARY CTA
           ═══════════════════════════════════════════════════════ */}
        {typeConfig.showCTA && (
          <div className="px-4 py-4 border-t border-gray-100">
            <button
              onClick={onCreateCase}
              disabled={actionInProgress === 'create'}
              className="w-full h-12 flex items-center justify-center gap-2 bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white text-sm font-bold rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-4 h-4" />
              {tr('Create Case', 'Opprett Sak')}
            </button>
            
            {/* Quick dismiss option for unknowns */}
            {emailType === 'unknown' && (
              <div className="flex items-center justify-center gap-4 mt-3">
                <button
                  onClick={() => handleDismiss('not_relevant')}
                  className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1.5 transition-colors"
                >
                  <ThumbsDown className="w-3.5 h-3.5" />
                  {tr('Not relevant', 'Ikke relevant')}
                </button>
              </div>
            )}
            
            {/* See full analysis link */}
            {onOpenFullAnalysis && (
              <button
                onClick={onOpenFullAnalysis}
                className="w-full text-center text-xs text-gray-500 hover:text-gray-700 py-2 transition-all font-medium"
              >
                {tr('See full analysis →', 'Se full analyse →')}
              </button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AIBriefSection;
